import { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, X, Save, Search, Upload, ImageIcon } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminAPI } from '../../utils/api';
import { useSettings } from '../../context/SettingsContext';
import toast from 'react-hot-toast';

const CATEGORIES = ['Laptops','Smartphones','Tablets','Accessories','Audio','Cameras','Gaming','Networking'];
const EMPTY = { name:'', brand:'', category:'Laptops', price:'', originalPrice:'', discount:0, stock:'', description:'', shortDescription:'', thumbnail:'', sku:'', warranty:'1 year', isFeatured:false, tags:'' };

export default function AdminProducts() {
  const { t } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [imgDragging, setImgDragging] = useState(false);
  const fileInputRef = useRef(null);

  const loadProducts = () => adminAPI.getProducts().then(r => setProducts(r.data.products)).catch(console.error).finally(() => setLoading(false));
  useEffect(() => { loadProducts(); }, []);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const openCreate = () => { setForm(EMPTY); setModal('new'); };
  const openEdit = (p) => { setForm({ ...p, tags: (p.tags || []).join(', ') }); setModal(p); };

  // Handle image file → base64
  const handleImageFile = (file) => {
    if (!file) return;
    if (!file.type.match(/image\/(png|jpeg|jpg|gif|webp)/)) {
      toast.error('Format non supporté. Utilise PNG, JPG ou GIF.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop lourde (max 5MB)');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      f('thumbnail', e.target.result);
      toast.success('Image chargée !');
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e) => handleImageFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setImgDragging(false);
    handleImageFile(e.dataTransfer.files[0]);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(',').map(s=>s.trim()).filter(Boolean) : [] };
      if (modal === 'new') await adminAPI.createProduct(payload);
      else await adminAPI.updateProduct(modal._id, payload);
      toast.success(modal === 'new' ? 'Produit créé !' : 'Produit mis à jour !');
      setModal(null); loadProducts();
    } catch (err) { toast.error(err.response?.data?.message || 'Erreur'); }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Désactiver "${name}" ?`)) return;
    try { await adminAPI.deleteProduct(id); toast.success('Désactivé'); loadProducts(); } catch { toast.error('Erreur'); }
  };

  const filtered = products.filter(p =>
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.brand.toLowerCase().includes(search.toLowerCase())) &&
    (!catFilter || p.category === catFilter)
  );

  const isBase64 = (str) => str && str.startsWith('data:image');

  return (
    <AdminLayout title={t('admin','products')}>
      <div className="admin-products-bar flex-between" style={{ marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', maxWidth: 320 }}>
            <Search size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="form-input" placeholder={t('admin','search')} value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 42, paddingTop: 10, paddingBottom: 10 }} />
          </div>
          <select className="form-input" value={catFilter} onChange={e => setCatFilter(e.target.value)} style={{ maxWidth: 200, paddingTop: 10, paddingBottom: 10 }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={17} /> {t('admin','add_product')}</button>
      </div>

      <div style={{ marginBottom: 12, color: 'var(--text-muted)', fontSize: 13 }}>{filtered.length} products</div>

      {loading ? <div className="flex-center" style={{ height: 300 }}><div className="spinner" /></div> : (
        <div className="card admin-table-wrap" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th><th>Status</th><th>Featured</th><th>{t('admin','actions')}</th></tr></thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p._id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {p.thumbnail ? (
                        <img src={p.thumbnail} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', background: 'var(--bg-secondary)', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: 48, height: 48, borderRadius: 10, background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <ImageIcon size={20} style={{ color: 'var(--text-muted)' }} />
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{p.brand} · SKU: {p.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td><span style={{ background: 'var(--accent-dim)', color: 'var(--accent)', padding: '4px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>{p.category}</span></td>
                  <td>
                    <div style={{ color: 'var(--gold)', fontWeight: 700 }}>${p.price.toLocaleString()}</div>
                    {p.discount > 0 && <div style={{ color: 'var(--danger)', fontSize: 12 }}>-{p.discount}%</div>}
                  </td>
                  <td><span style={{ fontWeight: 700, color: p.stock === 0 ? 'var(--danger)' : p.stock <= 5 ? 'var(--warning)' : 'var(--success)' }}>{p.stock}</span></td>
                  <td><span style={{ padding: '4px 10px', background: p.isActive ? 'rgba(34,197,94,0.1)' : 'rgba(244,63,94,0.1)', color: p.isActive ? 'var(--success)' : 'var(--danger)', borderRadius: 8, fontSize: 12, fontWeight: 600 }}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td style={{ textAlign: 'center' }}>{p.isFeatured ? '⭐' : '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}><Edit size={14} /></button>
                      <button className="btn btn-sm" style={{ background: 'rgba(244,63,94,0.1)', color: 'var(--danger)', border: 'none' }} onClick={() => handleDelete(p._id, p.name)}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>No products found</div>}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal-box" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700 }}>{modal === 'new' ? t('admin','add_product') : t('admin','edit_product')}</h2>
              <button onClick={() => setModal(null)} className="btn btn-ghost btn-icon"><X size={20} /></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSave}>
                <div className="grid-2" style={{ marginBottom: 0 }}>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Product Name *</label>
                    <input className="form-input" value={form.name} onChange={e => f('name', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Brand *</label>
                    <input className="form-input" value={form.brand} onChange={e => f('brand', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">SKU</label>
                    <input className="form-input" value={form.sku} onChange={e => f('sku', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-input" value={form.category} onChange={e => f('category', e.target.value)}>
                      {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Warranty</label>
                    <input className="form-input" value={form.warranty} onChange={e => f('warranty', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price ($) *</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={form.price} onChange={e => f('price', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Original Price ($)</label>
                    <input className="form-input" type="number" min="0" step="0.01" value={form.originalPrice} onChange={e => f('originalPrice', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Discount (%)</label>
                    <input className="form-input" type="number" min="0" max="99" value={form.discount} onChange={e => f('discount', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Stock *</label>
                    <input className="form-input" type="number" min="0" value={form.stock} onChange={e => f('stock', e.target.value)} required />
                  </div>

                  {/* ── IMAGE UPLOAD ── */}
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Image du produit</label>

                    {/* Drop zone */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setImgDragging(true); }}
                      onDragLeave={() => setImgDragging(false)}
                      onDrop={handleDrop}
                      style={{
                        border: `2px dashed ${imgDragging ? 'var(--accent)' : 'var(--border)'}`,
                        borderRadius: 14,
                        padding: '20px 16px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: imgDragging ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                        transition: 'all 0.2s',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 8,
                      }}
                    >
                      {form.thumbnail ? (
                        <img
                          src={form.thumbnail}
                          alt="preview"
                          style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 10, marginBottom: 6 }}
                        />
                      ) : (
                        <Upload size={32} style={{ color: 'var(--text-muted)' }} />
                      )}
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {form.thumbnail ? 'Cliquer pour changer l\'image' : 'Glisser-déposer ou cliquer pour importer'}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>PNG, JPG, GIF — max 5 MB</div>
                    </div>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />

                    {/* Remove image button */}
                    {form.thumbnail && (
                      <button
                        type="button"
                        onClick={() => f('thumbnail', '')}
                        style={{ marginTop: 8, fontSize: 12, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <X size={13} /> Supprimer l'image
                      </button>
                    )}
                  </div>
                  {/* ── END IMAGE UPLOAD ── */}

                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Tags (comma separated)</label>
                    <input className="form-input" value={form.tags} onChange={e => f('tags', e.target.value)} placeholder="laptop, apple, pro" />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Short Description</label>
                    <input className="form-input" value={form.shortDescription} onChange={e => f('shortDescription', e.target.value)} />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label className="form-label">Full Description *</label>
                    <textarea className="form-input" rows={4} value={form.description} onChange={e => f('description', e.target.value)} required />
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <input type="checkbox" id="featured" checked={form.isFeatured} onChange={e => f('isFeatured', e.target.checked)} style={{ width: 18, height: 18, accentColor: 'var(--accent)' }} />
                    <label htmlFor="featured" style={{ cursor: 'pointer', fontWeight: 500 }}>⭐ Featured Product</label>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>{t('common','cancel')}</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}><Save size={16} />{saving ? 'Saving...' : t('admin','save')}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}